/**
 * Strategy Canvas List
 *
 * Lists all Strategy Canvases in the workspace, following the zero/one/few/many
 * pattern. Gated by the strategy_canvas workspace module + STRATEGY_CANVAS_NAV flag.
 *
 * Route: /prioritization/strategy/canvas
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutTemplate,
  Plus,
  Loader2,
  AlertCircle,
  Trash2,
  ArrowRight,
  LayoutList,
  Table2,
  Calendar,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { StrategyConnectivityHelpButton } from './StrategyConnectivityDocModal';
import {
  useStrategyCanvasStore,
  type StrategyCanvas,
} from '../../../stores/prioritization/strategyCanvasStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { getItemCountState } from '../../../components/collection/types';

function deleteCanvasConfirmMessage(canvas: StrategyCanvas): string {
  const linked = canvas.linkedStrategyIds?.length ?? 0;
  let msg =
    `Delete the canvas "${canvas.title}"?\n\n` +
    `Only this canvas is removed from your workspace. Strategies, objectives, goals, and outcome drivers are not deleted.`;
  if (linked > 0) {
    msg += `\n\nThis canvas is linked to ${linked} strateg${linked === 1 ? 'y' : 'ies'}. Those links go away with the canvas, but the strategies themselves remain.`;
  }
  msg += `\n\nThis cannot be undone.`;
  return msg;
}

// ── Canvas Card ────────────────────────────────────────────────

function CanvasCard({
  canvas,
  onDelete,
}: {
  canvas: StrategyCanvas;
  onDelete: (id: string) => void;
}) {
  const [deleting, setDeleting] = useState(false);

  const BLOCKS = [
    { label: 'Vision',              value: canvas.vision },
    { label: 'Purpose',             value: canvas.purpose },
    { label: 'Mission',             value: canvas.mission },
    { label: 'Guiding Principles',  value: canvas.guidingPrinciples },
    { label: 'Competencies',        value: canvas.competencies },
    { label: 'Assumptions',         value: canvas.assumptions },
  ];

  const filledCount = BLOCKS.filter((b) => b.value?.trim()).length;

  async function handleDelete(e: React.MouseEvent) {
    e.preventDefault();
    if (!confirm(deleteCanvasConfirmMessage(canvas))) return;
    setDeleting(true);
    await onDelete(canvas.id);
  }

  return (
    <div className="group relative border border-[var(--rule)] bg-[var(--bg-raised)] p-5 hover:border-[var(--slate)] hover:shadow-sm transition-all flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <Link
          to={`/prioritization/strategy/canvas/${canvas.id}`}
          className="flex-1 min-w-0"
        >
          <div className="flex items-center gap-2">
            <LayoutTemplate className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
            <span className="font-semibold text-[var(--text-pri)] truncate text-sm group-hover:text-[var(--text-ter)]">
              {canvas.title}
            </span>
          </div>
        </Link>
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="opacity-0 group-hover:opacity-100 p-1.5 rounded text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-all"
          title="Delete canvas"
        >
          {deleting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      {/* Component fill indicator */}
      <div className="flex items-center gap-1.5">
        {BLOCKS.map((b) => (
          <div
            key={b.label}
            title={b.label}
            className={`h-1.5 flex-1 rounded-full ${
              b.value?.trim()
                ? 'bg-[var(--slate)]'
                : 'bg-[var(--hover-tint)]'
            }`}
          />
        ))}
      </div>
      <p className="text-xs text-[var(--text-ter)]">
        {filledCount} of {BLOCKS.length} sections filled
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between mt-auto pt-2 border-t border-[var(--rule)]">
        <div className="flex items-center gap-1 text-xs text-[var(--text-ter)]">
          <Calendar className="h-3 w-3" />
          {new Date(canvas.updatedAt).toLocaleDateString()}
        </div>
        <Link
          to={`/prioritization/strategy/canvas/${canvas.id}`}
          className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
        >
          Open
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

// ── Table Row ──────────────────────────────────────────────────

function CanvasTableRow({
  canvas,
  onDelete,
}: {
  canvas: StrategyCanvas;
  onDelete: (id: string) => void;
}) {
  const [deleting, setDeleting] = useState(false);

  const BLOCKS = [canvas.vision, canvas.purpose, canvas.mission, canvas.guidingPrinciples, canvas.competencies, canvas.assumptions];
  const filledCount = BLOCKS.filter(Boolean).length;

  async function handleDelete() {
    if (!confirm(deleteCanvasConfirmMessage(canvas))) return;
    setDeleting(true);
    await onDelete(canvas.id);
  }

  return (
    <tr className="hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors">
      <td className="px-4 py-3">
        <Link
          to={`/prioritization/strategy/canvas/${canvas.id}`}
          className="text-sm font-medium text-[var(--text-pri)] hover:text-[var(--text-ter)]"
        >
          {canvas.title}
        </Link>
        {/* Updated is drawn from sm up; below it, it reads here (audit #43). */}
        <span className="sm:hidden block mt-0.5 text-xs text-[var(--text-ter)]">
          Updated {new Date(canvas.updatedAt).toLocaleDateString()}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={`h-2 w-5  ${i < filledCount ? 'bg-[var(--slate)]' : 'bg-[var(--hover-tint)]'}`}
            />
          ))}
          <span className="ml-1.5 text-xs text-[var(--text-ter)]">{filledCount}/6</span>
        </div>
      </td>
      <td className="px-4 py-3 hidden sm:table-cell">
        <span className="text-xs text-[var(--text-ter)]">
          {new Date(canvas.updatedAt).toLocaleDateString()}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/prioritization/strategy/canvas/${canvas.id}`}
            className="text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
          >
            Open
          </Link>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="p-1 rounded text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-all"
          >
            {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Zero state ─────────────────────────────────────────────────

function ZeroState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-20 px-6">
      <div className="w-14 h-14 bg-[var(--slate-dim)] flex items-center justify-center mb-4">
        <LayoutTemplate className="h-7 w-7 text-[var(--text-ter)]" />
      </div>
      <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-2">
        No Strategy Canvases yet
      </h2>
      <p className="text-sm text-[var(--text-ter)] max-w-sm mb-6">
        Strategy Canvas is a one-page framework that ties together your Outcome Drivers, Vision,
        Purpose, Mission, and Objectives into a single view that makes complex change legible to your
        whole organization.
      </p>
      <button
        onClick={onCreate}
        className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] transition-colors"
      >
        <Plus className="h-4 w-4" />
        Create first canvas
      </button>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────

export function StrategyCanvasList() {
  const navigate   = useNavigate();
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);

  const canvases       = useStrategyCanvasStore((s) => s.canvases);
  const isLoading      = useStrategyCanvasStore((s) => s.isLoading);
  const syncError      = useStrategyCanvasStore((s) => s.syncError);
  const fetchCanvases  = useStrategyCanvasStore((s) => s.fetchCanvases);
  const createCanvas   = useStrategyCanvasStore((s) => s.createCanvas);
  const deleteCanvas   = useStrategyCanvasStore((s) => s.deleteCanvas);

  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');

  useEffect(() => {
    if (workspaceId) fetchCanvases();
  }, [workspaceId, fetchCanvases]);

  const countState = getItemCountState(canvases.length);
  const showTable = viewMode === 'table' || countState === 'many';

  async function handleCreate() {
    const { canvas } = await createCanvas({ title: 'Untitled Canvas' });
    if (canvas) navigate(`/prioritization/strategy/canvas/${canvas.id}`);
  }

  async function handleDelete(id: string) {
    await deleteCanvas(id);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<LayoutTemplate className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Strategy Canvas"
        subtitle="One-page framework for communicating complex change — Vision, Purpose, Mission, Guiding Principles, Competencies, and Assumptions unified with your live strategy objects."
        actions={
          <div className="flex items-center gap-2">
            <StrategyConnectivityHelpButton context="canvas_list" />
            {canvases.length > 0 ? (
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Canvas
            </button>
            ) : null}
          </div>
        }
      />

      {/* Error */}
      {syncError && (
        <div className="flex items-center gap-2 p-3 bg-[var(--coral-dim)] text-[var(--coral)] text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {syncError}
        </div>
      )}

      {/* Loading */}
      {isLoading && canvases.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
        </div>
      )}

      {/* Zero state */}
      {!isLoading && countState === 'zero' && (
        <ZeroState onCreate={handleCreate} />
      )}

      {/* One — single card with prompt to open */}
      {!isLoading && countState === 'one' && (
        <div className="max-w-sm">
          <CanvasCard canvas={canvases[0]} onDelete={handleDelete} />
        </div>
      )}

      {/* Few / Many — view toggle + grid/table */}
      {!isLoading && (countState === 'few' || countState === 'many') && (
        <>
          {/* Toolbar */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-[var(--text-ter)]">
              {canvases.length} canvas{canvases.length !== 1 ? 'es' : ''}
            </span>
            <div className="flex items-center gap-1 border border-[var(--rule)] p-0.5">
              <button
                onClick={() => setViewMode('card')}
                className={`p-1.5 rounded transition-colors ${viewMode === 'card' ? 'bg-[var(--hover-tint)] text-[var(--text-pri)]' : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'}`}
                title="Card view"
              >
                <LayoutList className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-1.5 rounded transition-colors ${showTable ? 'bg-[var(--hover-tint)] text-[var(--text-pri)]' : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'}`}
                title="Table view"
              >
                <Table2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          {!showTable ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {canvases.map((c) => (
                <CanvasCard key={c.id} canvas={c} onDelete={handleDelete} />
              ))}
            </div>
          ) : (
            <div className="overflow-hidden border border-[var(--rule)] bg-[var(--bg-raised)]">
              <table className="min-w-full divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                <thead>
                  <tr className="bg-[var(--surface)]">
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Title</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Completion</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide hidden sm:table-cell">Updated</th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                  {canvases.map((c) => (
                    <CanvasTableRow key={c.id} canvas={c} onDelete={handleDelete} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default StrategyCanvasList;
